/* product-form.js — wersja z obsługą gift-wrap + orphan-cleaner
   Założenia:
   - Checkbox i textarea prezentowe mają id:
       ExclusionsGiftChk-{{ section.id }}
       ExclusionsGiftTxt-{{ section.id }}
     (są w tym samym drzewie DOM co produkt; pobieramy je po section.id)
   - ID wariantu gift-wrap podaj przez:
       window.__GIFT_WRAP_VARIANT_ID = 1234567890
     LUB umieść element z atrybutem [data-gift-wrap-variant-id="1234567890"] w DOM.
   - Jeśli ID nie znajdziemy -> fallback do standardowego dodawania (bez gift).
*/

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');

        const sectionRoot = this.closest('[data-section]');
        this.sectionId = sectionRoot && sectionRoot.dataset ? sectionRoot.dataset.section : null;

        this.giftChk = this.sectionId
          ? document.getElementById(`ExclusionsGiftChk-${this.sectionId}`)
          : null;
        this.giftTA = this.sectionId
          ? document.getElementById(`ExclusionsGiftTxt-${this.sectionId}`)
          : null;

        this.variantIdInput.disabled = false;
        this.onSubmitHandler = this.onSubmitHandler.bind(this);
        this.form.addEventListener('submit', this.onSubmitHandler);

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton
          ? this.submitButton.querySelector('span')
          : null;

        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      // ---- submit z obsługą gift-wrap ----
      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (!this.submitButton) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const useGift = !!(this.giftChk && this.giftChk.checked);

        if (!useGift) {
          try {
            await this.addStandard(evt);
          } finally {
            this.finishSubmitState(evt, /*error*/ this.error === true);
          }
          return;
        }

        // --- dodawanie produktu + gift-wrap ---
        try {
          const fd = new FormData(this.form);
          const mainId = fd.get('id');
          const qty = Number(fd.get('quantity') || 1);

          const mainProps = {};
          fd.forEach((v, k) => {
            const m = k.match(/^properties\[(.+)\]$/);
            if (m) {
              const val = typeof v === 'string' ? v.trim() : v;
              if (val !== '') mainProps[m[1]] = val;
            }
          });

          const msgGift = (this.giftTA && this.giftTA.value ? this.giftTA.value : '').trim();
          mainProps['Pakowanie - Prezent'] = msgGift || 'Tak';

          // wygeneruj identyfikator grupy
          const giftGroup =
            (window.crypto && typeof window.crypto.randomUUID === 'function')
              ? window.crypto.randomUUID()
              : 'g' + Date.now() + Math.random().toString(16).slice(2);
          mainProps['__gift_group'] = giftGroup;

          const giftId = this.getGiftWrapVariantId();
          if (!giftId) {
            await this.addStandard(evt);
            return;
          }

          let sections = [];
          let sectionsUrl = window.location.pathname;
          if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
            sections = this.cart.getSectionsToRender().map((s) => s.id);
          }

          const addResp = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              items: [
                { id: mainId, quantity: qty, properties: mainProps },
                {
                  id: Number(giftId),
                  quantity: qty,
                  properties: {
                    'Gift wrap for': this.getProductTitleSafe(),
                    '__gift_group': giftGroup,
                    '__is_gift_wrap': '1'
                  }
                }
              ],
              sections,
              sections_url: sectionsUrl
            })
          });

          const response = await addResp.json();

          if (!addResp.ok || response.status) {
            const errMsg =
              response?.description ||
              response?.message ||
              'Wystąpił błąd dodawania do koszyka.';
            this.handleErrorMessage(errMsg);
            this.error = true;
            return;
          }

          const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
          if (!this.error) {
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: fd.get('id'),
              cartData: response
            });
            CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
          }

          CartPerformance.measure('add:paint-updated-sections', () => {
            this.cart?.renderContents(response);
          });

          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.show === 'function') drawer.show();

          this.error = false;
        } catch (e) {
          console.error(e);
          await this.addStandard(evt);
        } finally {
          this.finishSubmitState(evt, /*error*/ this.error === true);
        }
      }

      // ---- standardowa ścieżka ----
      addStandard(evt) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        return fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton &&
                this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              if (this.submitButtonText)
                this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          });
      }

      finishSubmitState(evt, isError) {
        this.submitButton.classList.remove('loading');
        if (this.cart && this.cart.classList.contains('is-empty'))
          this.cart.classList.remove('is-empty');
        if (!isError) this.submitButton.removeAttribute('aria-disabled');
        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.add('hidden');

        CartPerformance.measureFromEvent('add:user-action', evt);
      }

      getProductTitleSafe() {
        const titleEl =
          (this.sectionId &&
            document
              .querySelector(`#ProductInfo-${this.sectionId}`)
              ?.querySelector('.product__title h1')) ||
          document.querySelector('.product__title h1');

        const raw = titleEl ? titleEl.textContent : '';
        return (raw || '').trim();
      }

      getGiftWrapVariantId() {
        return 55795539214713;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText)
            this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

/* === Gift Wrap Orphan Cleaner (stabilny refresh po konflikcie z appkami) === */
(() => {
  const GIFT_WRAP_VARIANT_ID =
    (typeof window.__GIFT_WRAP_VARIANT_ID !== 'undefined' && Number(window.__GIFT_WRAP_VARIANT_ID)) ||
    (document.querySelector('[data-gift-wrap-variant-id]') &&
      Number(document.querySelector('[data-gift-wrap-variant-id]').getAttribute('data-gift-wrap-variant-id'))) ||
    55795539214713;

  const getCart = async () => {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' });
    if (!res.ok) throw new Error('Cart fetch failed');
    return res.json();
  };

  // Usuwanie po line_item.key
  const removeByKeys = async (keys) => {
    for (const key of keys) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: key, quantity: 0 })
        });
      } catch (e) {
        console.warn('[GiftWrapCleaner] remove failed for key', key, e);
      }
    }
  };

  // Licznik „epok” – pomaga wygrać wyścig z innymi rendererami
  let refreshEpoch = 0;

  // Odświeżenie sekcji koszyka – używamy kluczy po id (fallback: section)
  const refreshCartSections = async () => {
    const epoch = ++refreshEpoch; // zapamiętaj bieżącą epokę

    const cartEl = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    if (!cartEl || typeof cartEl.getSectionsToRender !== 'function' || typeof cartEl.renderContents !== 'function') {
      return;
    }

    const sectionsToRender = cartEl.getSectionsToRender(); // [{ id, section?, selector? }, ...]
    if (!Array.isArray(sectionsToRender) || !sectionsToRender.length) return;

    const keysForQuery = sectionsToRender
      .map(s => (s?.id || s?.section))
      .filter(Boolean);
    if (!keysForQuery.length) return;

    // 1) Pobierz HTML sekcji z cache-busterem
    const ts = Date.now();
    const url = `${window.location.pathname}?sections=${encodeURIComponent(keysForQuery.join(','))}&ts=${ts}`;
    const res = await fetch(url, { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' });
    if (!res.ok) return;
    const raw = await res.json();

    // 2) Zmapuj na payload oczekiwany przez renderContents
    const sectionsPayload = { sections: {} };
    for (const s of sectionsToRender) {
      const idKey = s?.id;
      const sectionKey = s?.section;
      if (idKey && raw[idKey]) { sectionsPayload.sections[idKey] = raw[idKey]; continue; }
      if (sectionKey && raw[sectionKey]) { sectionsPayload.sections[sectionKey] = raw[sectionKey]; }
    }

    // 3) Render tylko jeśli to dalej najnowsza epoka (nikt nas nie „przegonił”)
    if (epoch === refreshEpoch) {
      try {
        cartEl.renderContents(sectionsPayload);
        if (cartEl.tagName.toLowerCase() === 'cart-drawer' && typeof cartEl.show === 'function') {
          cartEl.show();
        }
      } catch (e) {
        console.warn('[GiftWrapCleaner] renderContents failed:', e);
      }
    }

    // 4) Drugi, opóźniony refresh – gdy inne appki zdążą nadpisać DOM
    setTimeout(async () => {
      if (epoch !== refreshEpoch) return; // w międzyczasie była nowsza mutacja
      try {
        const ts2 = Date.now();
        const res2 = await fetch(
          `${window.location.pathname}?sections=${encodeURIComponent(keysForQuery.join(','))}&ts=${ts2}`,
          { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' }
        );
        if (!res2.ok) return;
        const raw2 = await res2.json();
        const payload2 = { sections: {} };
        for (const s of sectionsToRender) {
          const idKey = s?.id;
          const sectionKey = s?.section;
          if (idKey && raw2[idKey]) { payload2.sections[idKey] = raw2[idKey]; continue; }
          if (sectionKey && raw2[sectionKey]) { payload2.sections[sectionKey] = raw2[sectionKey]; }
        }
        if (epoch === refreshEpoch) cartEl.renderContents(payload2);
      } catch (e) {
        console.warn('[GiftWrapCleaner] delayed refresh failed:', e);
      }
    }, 300);
  };

  const cleanupOrphanGiftWraps = async () => {
    try {
      const cart = await getCart();
      const groups = new Map();

      for (const item of cart.items || []) {
        const props = item.properties || {};
        const group = props.__gift_group || null;
        if (!group) continue;

        if (!groups.has(group)) groups.set(group, { mainQty: 0, wrapKeys: [] });
        const bucket = groups.get(group);

        const isWrap = props.__is_gift_wrap === '1' || item.variant_id === GIFT_WRAP_VARIANT_ID;
        if (isWrap) {
          bucket.wrapKeys.push(item.key);
        } else {
          bucket.mainQty += Number(item.quantity || 0);
        }
      }

      const orphanWrapKeys = [];
      for (const [, data] of groups) {
        if (data.mainQty <= 0 && data.wrapKeys.length) {
          orphanWrapKeys.push(...data.wrapKeys);
        }
      }

      if (orphanWrapKeys.length) {
        await removeByKeys(orphanWrapKeys);
        await refreshCartSections(); // refresh tuż po backendowej zmianie
        document.dispatchEvent(new CustomEvent('giftwrap:cleaned'));
      }
    } catch (e) {
      console.warn('[GiftWrapCleaner] cleanup failed:', e);
    }
  };

  // Debounce – poczekaj, aż wszystkie appki zakończą swoje „change/update”
  let debounceTimer = null;
  const scheduleCleanup = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(cleanupOrphanGiftWraps, 250);
  };

  // Interceptor fetch – odpal sprzątanie po każdej mutacji koszyka
  const originalFetch = window.fetch;
  if (!originalFetch.__giftWrapPatched) {
    window.fetch = async function(input, init) {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const isCartMutation = /\/cart\/(change|update|add)\.js(\?|$)/.test(url);
      const resp = await originalFetch.apply(this, arguments);
      if (isCartMutation) scheduleCleanup();
      return resp;
    };
    window.fetch.__giftWrapPatched = true;
  }

  // Eventy templatki/aplikacji
  document.addEventListener('cart:updated', scheduleCleanup);
  document.addEventListener('cart:change', scheduleCleanup);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleCleanup);
  } else {
    scheduleCleanup();
  }
})();
